import { styled } from "@stitches/react";
import { Filters } from "@/types/order-table";
import { RemovableTag } from "@/vendor/Forge/dist";
import { Button } from "@/vendor/Forge/dist";
import { useOrdersContext } from "@/contexts/orders-context";

interface Props {
  filters: Filters;
}

const Tags = styled("div", {
  display: "inline-flex",
  gap: 8,
  paddingBottom: 5,
  paddingTop: 8,
  flexWrap: "wrap",
  flexDirection: "row",
  alignItems: "flex-start",
});

const FilterShower = ({ filters }: Props) => {
  const { updateFilters, updateMultipleFilters } = useOrdersContext();

  const handleRemoveTag = (key: string) => {
    switch (key) {
      case "orderNames":
        updateFilters("orderNames", []);
        break;
      case "insurances":
        updateFilters("insurances", []);
        break;
      case "secondaryInsurances":
        updateFilters("secondaryInsurances", []);
        break;
      case "departments":
        updateFilters("departments", []);
        break;
      case "assignedUsers":
        updateFilters("assignedUsers", []);
        break;
      case "providerNames":
        updateFilters("providerNames", []);
        break;
      case "Perf Date":
        updateMultipleFilters({
          performDateRangeStar: [""],
          performDateRangeEn: [""],
        });
        break;
      case "patientNames":
        updateFilters("patientNames", []);
        break;
      case "clinicalFacilityDetails":
        updateFilters("clinicalFacilityDetails", []);
        break;
      case "orderingProvider":
        updateFilters("providerNames", []);
        break;
      case "statusList":
        updateFilters("statusList", []);
        break;
      case "assignedUser":
        updateFilters("assignedUser", []);
        break;
    }
  };
  const getLengthOfFilters = (key: string[] | undefined): string => {
    if (!key) return "";
    return key.length - 1 === 0 ? "" : ` +${key.length - 1} more`;
  };

  const clearAllFilters = () => {
    updateMultipleFilters({
      orderNames: [],
      departments: [],
      insurances: [],
      secondaryInsurances: [],
      clinicalFacilityDetails: [],
      providerNames: [],
      performDateRangeStar: [""],
      performDateRangeEn: [""],
      patientNames: [],
      assignedUser: [],
      priority: [],
      statusList: [],
    });
  };

  const excludedKeys = ["authStatus", "performDateRangeEn"];

  const getActiveFilterCategories = (filters: Filters): string[] => {
    return Object.entries(filters)
      .filter(([key, value]) => {
        if (excludedKeys.includes(key)) return false;
        if (!Array.isArray(value)) return false;

        // Check for at least one non-empty, trimmed string
        return value.some((v) => v?.toString().trim() !== "");
      })
      .map(([key]) => key);
  };

  const activeCategories = getActiveFilterCategories(filters);
  const filterCount = activeCategories.length;
  return (
    <>
      <Tags>
        <p style={{ fontWeight: "bold" }}>Filters: </p>
        {filters.orderNames && filters.orderNames?.length > 0 && (
          <RemovableTag
            key="orderNames"
            text={`Order name: ${
              filters.orderNames?.[0] ?? ""
            }, ${getLengthOfFilters(filters.orderNames)}`}
            onRemove={() => handleRemoveTag("orderNames")}
          />
        )}
        {filters.patientNames && filters.patientNames?.length > 0 && (
          <RemovableTag
            key="patientNames"
            text={`Patient: ${
              filters.patientNames?.[0] ?? ""
            },${getLengthOfFilters(filters.patientNames)}`}
            onRemove={() => handleRemoveTag("patientNames")}
          />
        )}
        {filters.insurances && filters.insurances?.length > 0 && (
          <RemovableTag
            key="insurances"
            text={`Primary: ${
              filters?.insurances?.[0] ?? ""
            },${getLengthOfFilters(filters?.insurances)}`}
            onRemove={() => handleRemoveTag("insurances")}
          />
        )}
        {filters.secondaryInsurances && filters.secondaryInsurances?.length > 0 && (
          <RemovableTag
            key="secondaryInsurances"
            text={`Secondary: ${
              filters.secondaryInsurances?.[0] ?? ""
            },${getLengthOfFilters(filters.secondaryInsurances)}`}
            onRemove={() => handleRemoveTag("secondaryInsurances")}
          />
        )}
        {filters.clinicalFacilityDetails && filters.clinicalFacilityDetails?.length > 0 && (
          <RemovableTag
            key="clinicalFacilityDetails"
            text={`Rad Facility: ${
              filters.clinicalFacilityDetails?.[0] ?? ""
            },${getLengthOfFilters(filters.clinicalFacilityDetails)}`}
            onRemove={() => handleRemoveTag("clinicalFacilityDetails")}
          />
        )}
        {filters.providerNames && filters.providerNames?.length > 0 && (
          <RemovableTag
            key="orderingProvider"
            text={`Ord Provider: ${
              filters.providerNames?.[0] ?? ""
            },${getLengthOfFilters(filters.providerNames)}`}
            onRemove={() => handleRemoveTag("orderingProvider")}
          />
        )}
        {filters.departments && filters.departments?.length > 0 && (
          <RemovableTag
            key="departments"
            text={`Dept: ${filters.departments?.[0] ?? ""}, 
            ${getLengthOfFilters(filters.departments)}`}
            onRemove={() => handleRemoveTag("departments")}
          />
        )}
        {filters.performDateRangeStar && filters.performDateRangeStar[0] != "" && (
          <RemovableTag
            key="Perf Date"
            text={`Perf Date: ${filters.performDateRangeStar?.[0] ?? ""} - ${
              filters.performDateRangeEn?.[0] ?? ""
            },
            )}`}
            onRemove={() => handleRemoveTag("Perf Date")}
          />
        )}
        {filters.assignedUser && filters.assignedUser?.length > 0 && (
          <RemovableTag
            key="AssignedUser"
            text={`Assign: ${filters.assignedUser?.[0] ?? ""}, 
            ${getLengthOfFilters(filters.assignedUser)}`}
            onRemove={() => handleRemoveTag("assignedUser")}
          />
        )}
        {filters.statusList && filters.statusList?.length > 0 && (
          <RemovableTag
          key="statusList"
          text={`Status: ${filters.statusList?.[0] ?? ""}...`}
          onRemove={() => handleRemoveTag("statusList")}
        />
        )}

        <Button variant="tertiary" onClick={clearAllFilters}>
          Clear all filters ({filterCount})
        </Button>
      </Tags>
    </>
  );
};

export default FilterShower;
